/**
 * Calendar arithmetic on the service day, derived ONCE.
 *
 * A `ServiceDay` is the platform's central calendar vocabulary and `common.ts`
 * declares it as a validated wire string with zero operations - so every
 * surface that needed to move one by a day wrote its own. Fourteen named
 * helpers under nine names, plus the inline loops, and they did not agree on
 * the anchor: nine parsed at `T12:00:00Z`, one at `T00:00:00Z`, four did
 * component arithmetic through `Date.UTC`.
 *
 * They agree on every date the platform can actually hold - measured, not
 * assumed: all four variants return the same string for every `YYYY-MM-DD`
 * from 1990 to 2110 against fifteen deltas (662,910 pairs, zero
 * disagreements), because the arithmetic runs in UTC where every day is
 * exactly 24 hours and no DST rule applies. The test beside this file re-runs
 * that comparison over a decade rather than the whole span, which is the part
 * worth paying for on every commit. What they did
 * NOT agree on is the edge: `Date.UTC(year, ...)` maps a two-digit year onto
 * the 1900s (`0099-01-01` shifts to `1999-01-02`), and a malformed day came
 * back as a `RangeError` from nine of them, as the input unchanged from three,
 * and as the string `NaN-NaN-NaN` from one. That is the disagreement this file
 * closes, and the reason it is one function rather than fourteen.
 *
 * The anchor kept is midday UTC, which is what nine of the fourteen used and
 * what `weekdayOfServiceDay` has always used: a service day is a LABEL, not an
 * instant, and midday is the reading furthest from either edge of any zone
 * offset a caller might later apply to it.
 *
 * Pure and I/O-free, stated over the wire string, like everything else in this
 * directory - which is what lets the worker, all four frontends, `routing` and
 * `services` share one implementation.
 */

/** `YYYY-MM-DD`, the shape `ServiceDay` (`common.ts`) validates to. */
const SERVICE_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One `Intl.DateTimeFormat` per time zone, forever. Constructing one costs
 * about a millisecond (V8 compiles the zone's rules each time) while
 * `format` on an existing instance is microseconds, and this runs on the guest
 * hot path once per candidate morning. Memoization of a deterministic
 * constructor - the module stays pure and I/O-free.
 *
 * The i18n spelling this replaced built a formatter on EVERY call.
 */
const serviceDayFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * Which service day an INSTANT falls on, read in `timeZone` - the one question
 * every "today" on this platform is asking, and the one place it is answered.
 *
 * There were three spellings of it: `isoDateInTimeZone` (`@croissants/domain`),
 * `toServiceDay` (`@croissants/i18n`) and the `day` half of `localParts`
 * (the worker's `wire.ts`), which used three different formatter locales, two
 * caching strategies and one none at all. They agreed; nothing made them. All
 * three are gone and this is the only one left.
 *
 * It lives HERE rather than in `domain` because this is the only package every
 * caller may reach: `domain` is a server-only layer that the four frontends are
 * forbidden to import (`scripts/check-import-graph.mjs` rule (c)), while
 * `@croissants/contracts/projections` is already imported by the worker, all
 * four frontends, `i18n`, `routing` and `services`. `domain` itself depends on
 * nothing and so could never have delegated to this file - which is why its
 * twin was retired by moving its callers here rather than by rewriting it, and
 * why `service-day-in-zone.test.ts` in `routing`, the equality pin that held
 * the two to one answer while both existed, went with it.
 *
 * `en-CA` is the locale whose date format IS `YYYY-MM-DD`; the alternative is
 * assembling `formatToParts` by hand, which is what two of the three copies did
 * differently.
 */
export function serviceDayInTimeZone(instant: Date, timeZone: string): string {
  let formatter = serviceDayFormatters.get(timeZone);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    serviceDayFormatters.set(timeZone, formatter);
  }
  return formatter.format(instant);
}

/** `YYYY-MM`, the period payouts, commissions and earnings are cut on. */
const MONTH_PATTERN = /^\d{4}-\d{2}$/;

/**
 * The label as an instant, or `null` when it names no morning.
 *
 * Midday UTC, never midnight and never the local components. Midnight puts the
 * reading one side or the other of a zone offset, which is how a Sunday opening
 * time once rendered as Saturday's; the components go through `Date.UTC`, whose
 * two-digit-year rule silently moves any year below 100 into the twentieth
 * century.
 *
 * THE ROUND TRIP is what makes this as strict as the wire, and it is the whole
 * of the check. `Date` refuses `2026-13-01`, `2026-00-10` and `2026-08-32` -
 * and it does NOT refuse `2026-02-30`, it ROLLS it: `new
 * Date('2026-02-30T12:00:00.000Z')` is 2 March. `ServiceDay` in `common.ts` is
 * `z.iso.date()`, which refuses all four, so a label accepted here and refused
 * at the boundary is a label one surface silently answers for a DIFFERENT
 * MORNING while every other one rejects it - a guest strip that opened on 2
 * March for a URL saying 30 February. Formatting the instant back and comparing
 * it to the label closes exactly that gap and nothing else: every real day
 * round-trips.
 */
function parseServiceDay(serviceDay: string): Date | null {
  if (!SERVICE_DAY_PATTERN.test(serviceDay)) return null;
  const at = new Date(`${serviceDay}T12:00:00.000Z`);
  if (Number.isNaN(at.getTime())) return null;
  return at.toISOString().slice(0, 10) === serviceDay ? at : null;
}

function anchorOf(serviceDay: string): Date {
  const at = parseServiceDay(serviceDay);
  if (at === null) {
    throw new RangeError(`Expected a service day 'YYYY-MM-DD', got '${serviceDay}'`);
  }
  return at;
}

/**
 * Whether a string is a service day this module will accept - the question to
 * ask BEFORE the arithmetic, on any value a person could have typed.
 *
 * The shape is not the date: `2026-13-01`, `2026-00-10`, `2026-08-32` and
 * `2026-02-30` all match `YYYY-MM-DD` and name no morning, and every one of
 * them can arrive from a hand-edited or bookmarked URL. A screen that shifts
 * one of those without asking gets a `RangeError` in its render, and no app
 * here mounts an error boundary above it, so the whole page goes blank. Ask
 * this and show the reader an empty filter instead.
 *
 * The fourth is the one a bare `Date` misses and the one this used to answer
 * TRUE for. V8 rolls a too-large day of a real month into the next one, so
 * `2026-02-30` parsed as 2 March and `addServiceDays('2026-02-30', 0)` answered
 * `'2026-03-02'` - a date the reader never named, from a label the wire's
 * `z.iso.date()` refuses outright. That made this predicate weaker than the
 * boundary it stands in front of, which is the one thing a pre-flight check
 * must never be. It is stated over `parseServiceDay` now, exactly as `anchorOf`
 * is, so the two cannot disagree with each other or with the wire.
 */
export function isServiceDay(value: string): boolean {
  return parseServiceDay(value) !== null;
}

/**
 * The service day `days` away from `serviceDay`, over the calendar and never
 * over an instant.
 *
 * `days` may be negative; `addServiceDays(day, -1)` is the day before. Throws a
 * `RangeError` on anything that is not a `YYYY-MM-DD` label, because a surface
 * that has been handed a malformed day is a surface that will draw a wrong
 * date, and one of the copies this replaced answered `NaN-NaN-NaN`.
 */
export function addServiceDays(serviceDay: string, days: number): string {
  const at = anchorOf(serviceDay);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * THE PLATFORM'S OWN ZONE: the one every shop-less clock reads - the jobs, the
 * operations dashboards, the partner's month (PAY-025), a fleet device paired
 * to no shop, and the default a new shop is created with. A shop that carries
 * a timezone of its own is read in THAT zone, never in this one.
 *
 * Written once because it was written forty times - five local constants and
 * thirty-five literals across the worker - and a constant restated at forty
 * call sites is forty places for one of them to drift.
 */
export const PLATFORM_TIME_ZONE = 'Europe/Paris';

/**
 * The service day after the one `now` falls on, in `timeZone`.
 *
 * A calendar day after today rather than 24 hours after now: across a DST
 * change the two land on different mornings - `now + 24 h` from 23:30 Paris on
 * 28 March 2026 is already 30 March, and from 00:30 Paris on 25 October it is
 * still the 25th.
 */
export function nextServiceDay(now: Date, timeZone: string): string {
  return addServiceDays(serviceDayInTimeZone(now, timeZone), 1);
}

/** `Date#getDay`'s indexing: 0 = Sunday .. 6 = Saturday. */
export type ServiceDayWeekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * The weekday a service day falls on, indexed the way the `earliest_ready` /
 * `latest_pickup` tables are keyed (0 = Sunday).
 *
 * Deliberately NOT strict, unlike `addServiceDays`: its callers index a
 * seven-member record with the answer, and a malformed day has always read
 * there as "no hours on that morning" rather than as a thrown request. The
 * wire's own `PickupWindowRule` counts the ISO way (1 = Monday); that
 * conversion stays in the bakery route and is not duplicated here.
 */
export function weekdayOfServiceDay(serviceDay: string): ServiceDayWeekday {
  return new Date(`${serviceDay}T12:00:00.000Z`).getUTCDay() as ServiceDayWeekday;
}

/** The `YYYY-MM` month a service day falls in. */
export function monthOf(serviceDay: string): string {
  return serviceDay.slice(0, 7);
}

function partsOfMonth(month: string): readonly [number, number] {
  if (!MONTH_PATTERN.test(month)) {
    throw new RangeError(`Expected a month 'YYYY-MM', got '${month}'`);
  }
  const [year, monthNumber] = month.split('-').map(Number) as [number, number];
  return [year, monthNumber];
}

/**
 * `Date.UTC` with the two-digit-year rule taken off: it maps a year of 0..99
 * onto 1900+year, so `Date.UTC(75, 5, 15)` is 1975 and a `0075-06-15` label
 * would come back as a date a century away. `setUTCFullYear` has no such rule,
 * and takes the month and day in the same call so the intermediate date can
 * never roll a month of its own.
 */
function utcDate(year: number, monthIndex: number, day: number): Date {
  const at = new Date(0);
  at.setUTCFullYear(year, monthIndex, day);
  at.setUTCHours(12, 0, 0, 0);
  return at;
}

/**
 * The first and last service day of a `YYYY-MM` month, both inclusive.
 *
 * Day 0 of the following month is the last day of this one, so it is leap-year
 * correct because the platform never counts the days itself (PAY-025, CON-006).
 */
export function monthBounds(month: string): { readonly start: string; readonly end: string } {
  const [year, monthNumber] = partsOfMonth(month);
  const last = utcDate(year, monthNumber, 0);
  return { start: `${month}-01`, end: `${month}-${String(last.getUTCDate()).padStart(2, '0')}` };
}

/**
 * The `count` most recent months ending at `month`, NEWEST FIRST.
 *
 * The period selector on every money screen offers exactly this list, and the
 * worker cut it four separate times from a `Date` it had built for the purpose.
 *
 * BOTH halves are padded. The month was and the year was not, so every answer
 * this function gave was a `YYYY-MM` only while the year happened to have four
 * digits - and `utcDate` above exists precisely so a year below 100 is NOT
 * silently moved into the 1900s, which means a three- or two-digit year reaches
 * this line intact and left as `999-12`. That string matches no `MONTH_PATTERN`
 * anywhere, so it would have come back out of this module and been refused by
 * the next function to receive it.
 */
export function recentMonths(month: string, count: number): readonly string[] {
  const [year, monthNumber] = partsOfMonth(month);
  const months: string[] = [];
  for (let back = 0; back < count; back += 1) {
    const at = utcDate(year, monthNumber - 1 - back, 1);
    const paddedYear = String(at.getUTCFullYear()).padStart(4, '0');
    months.push(`${paddedYear}-${String(at.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

/**
 * Every service day from `from` to `to`, both ends included, ascending.
 *
 * `cap` bounds the walk on input nobody has validated - the guest strip is
 * built from an answer that carries at most sixty mornings, and a loop over
 * two dates is only ever as trustworthy as the further of them. An empty list
 * comes back when `to` is before `from`.
 */
export function serviceDaysBetween(from: string, to: string, cap = 366): readonly string[] {
  const end = anchorOf(to).getTime();
  const cursor = anchorOf(from);
  const days: string[] = [];
  while (days.length < cap && cursor.getTime() <= end) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}
